import { PaginatedApiResponse, PaginationResponse } from "./types";

/**
 * Defines what keys are present in the API
 * Use this so we don't have magic strings all over the shop
 */
export enum EnvKeys {
	JwtCookieKey,
	RefreshCookieKey,
	UserCookieKey,
	LampUrl,
	LampAttorneyUrl,
	PassUrl,
	LampCaseUrl,
	MemberPassUrl,
	NetworkRate,
	NetworkRateParalegal,
	AzureClientId,
	AzureTenantId,
	SSODomains
}

/**
 * Quite literally an empty function. Avoids eslint no-empty-function problems
 * @returns undefined
 */
export const emptyFunc = () => undefined;

export const dummyPaginatedApiResponse: PaginatedApiResponse = {
	data: [],
	meta: {} as PaginationResponse
};

export enum ClaimStatus {
	Draft = "Draft",
	Submitted = "Submitted",
	InReview = "In Review",
	Approved = "Approved",
	Denied = "Denied",
	Archived = "Archived",
	Cancelled = "Cancelled",
	ClosedNotPaid = "Closed - Not Paid",
	ClosedNotPaidGoodwill = "Closed - Not Paid Goodwill",
	ClosedPaid = "Closed - Paid",
	QueuedForPayment = "Queued For Payment",
	PendingProvider = "Pending Provider",
	PendingVerification = "Pending Verification",
	PendingInternalReview = "Pending Internal Review",
	Duplicate = "Duplicate"
}

export enum ChecklistStatus {
	Eligibility = "Eligibility",
	Invoice = "Invoice",
	SupportingDocuments = "SupportingDocuments",
	Confirmed = "Confirmed"
}

export enum ClaimServiceProvidedFor {
	PrimaryMember = "Primary Member",
	Spouse = "Spouse",
	DomesticPartner = "Domestic Partner",
	DependentChild = "Dependent Child",
	ElderParent = "Elder Parent",
	Other = "Other"
}

export enum BillingType {
	EstatePlanning = "EstatePlanning",
	FixedHighValue = "FixedHighValue",
	FixedLowValue = "FixedLowValue",
	Hourly = "Hourly",
	HourlyOrFixed = "HourlyOrFixed"
}

export enum UsesPerYearDefinition {
	Total = "Total",
	PerCoveredPerson = "PerCoveredPerson"
}

export enum DocumentCategory {
	Invoices = "Invoices",
	SupportingDocuments = "SupportingDocuments",
	ClaimForms = "ClaimForms",
	ProofOfFullTimeSchoolEnrollment = "ProofOfFullTimeSchoolEnrollment",
	Ccs = "Ccs",
	Denied = "Denied",
	AuditDocuments = "AuditDocuments"
}

export enum UserRole {
	Admin = "Admin",
	ClaimsAdmin = "ClaimsAdmin",
	ClaimsAnalyst = "ClaimsAnalyst",
	ClaimsManager = "ClaimsManager",
	ClaimsViewOnly = "ClaimsViewOnly",
	ClaimsNetworkManager = "ClaimsNetworkManager",
	Developer = "Developer",
	Member = "Member",
	MembersServices = "MembersServices",
	Provider = "Provider",
	ProvidersServices = "ProvidersServices",
	Payments = "Payments"
}

/**
 * Object to hold footer styles so we don't copy/paste the same thing 400 times
 */
export const footerStyles: React.CSSProperties = {
	width: "-webkit-fill-available",
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start"
};

// Roles allowed to create claims
export const claimsRoles: Array<string> = [UserRole.ClaimsAnalyst, UserRole.ClaimsManager, UserRole.ClaimsAdmin];

// File size limits for security
export const FILE_SIZE_LIMITS = {
	MAX_FILE_SIZE: process.env.REACT_APP_MAX_FILE_SIZE
		? parseInt(process.env.REACT_APP_MAX_FILE_SIZE) * 1024 * 1024 // Convert MB to bytes
		: 500 * 1024 * 1024, // 500MB per file
	MAX_CLAIM_SIZE: process.env.REACT_APP_MAX_CLAIM_SIZE
		? parseFloat(process.env.REACT_APP_MAX_CLAIM_SIZE) * 1024 * 1024 * 1024 // Convert GB to bytes
		: 2.5 * 1024 * 1024 * 1024 // 2.5GB per claim
} as const;

export enum ClaimsListingTabRoutes {
	AnalystDiary = "/claims/analystdiary",
	ManagerDiary = "/claims/managerdiary",
	AdminDiary = "/claims/admindiary",
	Assigned = "/claims/assigned",
	All = "/claims/all"
}
/**
 * Claim Reservations Constants
 */

// Roles allowed to manage claim reservations
export const reservationManagementRoles: Array<string> = [UserRole.ClaimsManager];

// Reservation status options
export const RESERVATION_STATUS = {
	ACTIVE: "active",
	INACTIVE: "inactive"
} as const;

// Reservation success messages
export const RESERVATION_SUCCESS_MESSAGES = {
	CREATED: "Auto-assignment rule created successfully",
	REMOVED: "Auto-assignment rule removed",
	RECOVERED: "Auto-assignment rule recovered"
} as const;

// Reservation error messages
export const RESERVATION_ERROR_MESSAGES = {
	DUPLICATE:
		"A reservation with this combination already exists. Please check the list or recover a deleted reservation.",
	NOT_ANALYST: "Selected user must be a Claims Analyst",
	CONSTRAINT_ERROR: "Unable to create reservation. This combination may already exist (including deleted items).",
	GENERAL_ERROR: "An error occurred while processing your request"
} as const;

// Activity type for auto-assignments
export const ACTIVITY_TYPE_AUTO_ASSIGNMENT = "AutoAssignment";
